import React, { useEffect } from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";

/**
 * Turn one entry of the `chips` prop into a Chip.
 *
 * Entries are either a bare string ("storageaccounts") or an object
 * ({ label, color, icon }) when the caller wants a semantic colour. Anything
 * falsy is dropped, because detail pages mount before their fetch resolves and
 * `Element?.subResourceType` is undefined on that first render - the whole
 * point of the chips is to stop the header showing machine strings, so it must
 * not show an empty chip either.
 */
const toChip = (chip, index) => {
  if (chip === null || chip === undefined || chip === false || chip === "") {
    return null;
  }

  const spec = typeof chip === "object" ? chip : { label: chip };

  if (!spec.label) {
    return null;
  }

  return (
    <Chip
      key={spec.key ?? `${spec.label}-${index}`}
      label={spec.label}
      color={spec.color ?? "default"}
      icon={spec.icon}
      size="small"
      // Filled, matching NiceOpportunityStage / NiceComplexity. A second
      // (outlined) chip vocabulary in the same app reads as an accident.
      variant="filled"
    />
  );
};

/**
 * Page header.
 *
 * `title` / `subtitle` / `count` / `titleUpdater` / `subtitleUpdater` behave as
 * they always did - there are 82 call sites and only the entity detail pages
 * are being restructured here.
 *
 * Two additions:
 *   `chips`   - classification of the thing on the page (its type, its stage),
 *               rendered as chips instead of the concatenated machine string
 *               detail pages used to push through `subtitle`.
 *   `backTo`  - route of the list this record belongs to. A detail page's only
 *               way out was the breadcrumb's parent segment, which is a small
 *               text target for what is usually the next thing a user wants.
 *               An explicit route rather than navigate(-1): history-back walks
 *               out of the app entirely when the page was deep-linked or
 *               arrived at from an external link.
 *   `actions` - page level buttons. Previously every detail scene wrapped
 *               <Header> and a button cluster in its own <FlexBetween>, so the
 *               buttons floated at the far right edge of the viewport with no
 *               relationship to the title. Passing them here groups them with
 *               the header and lets them wrap under it on a narrow viewport.
 */
const Header = ({
  title,
  subtitle,
  count,
  chips,
  actions,
  backTo,
  backLabel,
  titleUpdater,
  subtitleUpdater,
}) => {
  // Was a bare assignment in the render body - a side effect during render, and
  // it wrote the literal string "undefined" into the tab title on every detail
  // page for as long as the fetch was in flight.
  useEffect(() => {
    if (typeof title === "string" && title.length > 0) {
      document.title = title;
    }
  }, [title]);

  const chipList = (Array.isArray(chips) ? chips : [chips])
    .map(toChip)
    .filter(Boolean);

  const hasStrapline = chipList.length > 0 || Boolean(subtitle);

  return (
    <Box
      component="header"
      sx={{
        mb: ".5rem",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "flex-start" },
        justifyContent: "space-between",
        gap: "1rem",
        // Only stretch when there is an actions slot to push right. Without
        // this, the ~70 call sites that still nest <Header> inside their own
        // <FlexBetween> would have their sibling buttons squeezed.
        width: actions ? "100%" : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: ".5rem", minWidth: 0 }}>
        {backTo ? (
          <Tooltip title={backLabel || "Back"}>
            <IconButton
              component={RouterLink}
              to={backTo}
              aria-label={backLabel || "Back"}
              size="small"
              // Nudged down to sit on the title's optical centre rather than
              // its box top, since the title is h3 and the button is small.
              sx={{ mt: ".15rem", color: "text.secondary", flexShrink: 0 }}
            >
              <ArrowBackOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: ".3rem", minWidth: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            gap: ".4rem",
            alignItems: "baseline",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h3"
            color="text.primary"
            fontWeight="bold"
            sx={{ m: 0 }}
          >
            {title}
          </Typography>

          {count ? (
            <Typography variant="h5" color="text.secondary" fontWeight="bold">
              ({count})
            </Typography>
          ) : null}
        </Box>

        {hasStrapline && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            {chipList}
            {subtitle ? (
              <Typography variant="h5" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Box>
      </Box>

      {actions ? (
        <Box
          data-testid="header-actions"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: ".5rem",
            flexShrink: 0,
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
};

export default Header;
